import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ShieldCheck, ShieldX, Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { SimpleAuthShell, primaryBtn } from "@/components/SimpleAuthShell";

const fmt = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

export default function VerifyCertificatePage() {
  const { code } = useParams();
  const nav = useNavigate();
  const [value, setValue] = useState(code || "");
  const [state, setState] = useState({ status: code ? "loading" : "idle" });

  useEffect(() => {
    if (!code) return setState({ status: "idle" });
    setState({ status: "loading" });
    api.get(`/certificates/verify/${encodeURIComponent(code)}`)
      .then((r) => setState({ status: "ok", cert: r.data }))
      .catch(() => setState({ status: "notfound" }));
  }, [code]);

  const submit = (e) => {
    e.preventDefault();
    if (value.trim()) nav(`/verifier/${value.trim().toUpperCase()}`);
  };

  return (
    <SimpleAuthShell title="Vérifier un certificat">
      <form onSubmit={submit} className="space-y-3" data-testid="verify-form">
        <p className="text-slate-600">Entrez le numéro inscrit sur le certificat.</p>
        <div className="relative">
          <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={value} onChange={(e) => setValue(e.target.value.toUpperCase())} maxLength={12} placeholder="Ex : 3FA9C21B" className="h-12 pl-10 text-lg font-bold tracking-widest" data-testid="verify-code-input" />
        </div>
        <button type="submit" className={primaryBtn} data-testid="verify-submit-btn">Vérifier</button>
      </form>
      {state.status === "loading" && <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto" />}
      {state.status === "notfound" && (
        <div className="rounded-2xl bg-red-50 border-2 border-red-300 p-4 flex gap-3 text-red-800" data-testid="verify-notfound">
          <ShieldX className="w-8 h-8 shrink-0" />
          <p className="font-semibold">Aucun certificat ne correspond à ce numéro.</p>
        </div>
      )}
      {state.status === "ok" && (
        <div className="rounded-2xl bg-emerald-50 border-2 border-emerald-400 p-5 space-y-2 text-emerald-900" data-testid="verify-result">
          <p className="flex items-center gap-2 font-display text-2xl font-bold uppercase"><ShieldCheck className="w-7 h-7" /> Certificat authentique</p>
          <p className="text-lg"><b data-testid="verify-name">{state.cert.name}</b> · {state.cert.trade}</p>
          <p data-testid="verify-level">Niveau {state.cert.level_from_label} → <b>{state.cert.level_to_label}</b></p>
          <p className="text-sm">{state.cert.lessons} leçons · {state.cert.words} mots · {state.cert.average}% de réussite</p>
          <p className="text-sm">Délivré le {fmt(state.cert.issued_at)} · N° {state.cert.code}</p>
        </div>
      )}
    </SimpleAuthShell>
  );
}
